import { XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import {
  MAX_ALLOWED_SIZE,
  MAX_ALLOWED_UPLOADS,
  MIN_ALLOWED_UPLOADS,
  VEHICLE_IMAGE_UPLOAD_PRESET,
} from "../utils/constants";
import Spinner from "./Spinner";

import Uppy, { UppyFile } from "@uppy/core";
import XHR from "@uppy/xhr-upload";
import { Dashboard } from "@uppy/react";
import Spanish from "@uppy/locales/lib/es_MX";

import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import { Container } from "./DraggableContainer";

const uppy = new Uppy({ locale: Spanish, autoProceed: true }).use(XHR, {
  endpoint: process.env.NEXT_PUBLIC_CLOUDINARY_BASE_URL,
  allowedMetaFields: ["upload_preset", "tags"],
});

type TProps = {
  id: string;
  tags: string;
  doDiscard?: boolean;
  previewUrls?: string[];
  invalid?: boolean;
  onChange?: (urls: string[]) => void;
  disabled?: boolean;
  label?: string;
  labelSuggestion?: string;
  dropzoneIcon?: (props: any) => JSX.Element;
  min?: number;
  max?: number;
  preset?: string;
  setImagesWereReordered: Dispatch<SetStateAction<boolean>>;
  setImagesToDeleteFromCloudinary: Dispatch<SetStateAction<string[]>>;
  onFilesAdded?: (
    files: UppyFile<Record<string, unknown> | Record<string, unknown>>[]
  ) => void;
};

export default function FileUploadUppy({
  id,
  tags,
  previewUrls = [],
  doDiscard,
  invalid,
  onChange,
  disabled,
  label,
  labelSuggestion,
  min,
  max,
  preset,
  setImagesWereReordered,
  setImagesToDeleteFromCloudinary,
  onFilesAdded,
}: TProps) {
  const [urls, setUrls] = useState<string[]>([]);
  const [hasSetPreviews, setHasSetPreviews] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  // const { toggleToast } = useToast();

  const minAllowed = min || MIN_ALLOWED_UPLOADS;
  const maxAllowed = max || MAX_ALLOWED_UPLOADS;
  // const hasReachedMax = previews.length >= maxAllowed;

  useEffect(() => {
    uppy.setOptions({
      id: `uppy-${id}`,
      restrictions: {
        maxNumberOfFiles: maxAllowed,
        minNumberOfFiles: minAllowed,
        maxFileSize: MAX_ALLOWED_SIZE * 1000000,
        allowedFileTypes: [
          "image/png",
          "image/jpeg",
          "image/webp",
          "image/heif",
          "image/heic",
        ],
      },
      autoProceed: true,
    });

    uppy.on("file-added", (file) => {
      uppy.setFileMeta(file.id, {
        upload_preset: preset || VEHICLE_IMAGE_UPLOAD_PRESET,
        tags,
      });
    });

    uppy.on("upload-success", (file, response) => {
      setUrls([...urls, response.body.secure_url]);
      setPreviews([...previews, response.body.secure_url]);
    });

    uppy.on("files-added", () => {
      onFilesAdded?.(uppy.getFiles());
    });
  }, [urls]);

  useEffect(() => {
    return () => {
      uppy.cancelAll();
    };
  }, []);

  useEffect(() => {
    if (onChange) {
      setImagesWereReordered(true);
      onChange(urls);
    }
  }, [urls]);

  useEffect(() => {
    if (doDiscard) {
      setPreviews(previewUrls);
    }
  }, [doDiscard]);

  useEffect(() => {
    reorderUrls();
  }, [previews]);

  useEffect(() => {
    if (!hasSetPreviews && previewUrls?.length) {
      setUrls(previewUrls);
      setPreviews(previewUrls);
      setHasSetPreviews(true);
    }
  }, [previewUrls]);

  function reorderUrls() {
    let newUrls: string[] = [];
    previews.forEach((preview) => {
      newUrls.push(preview);
    });
    setUrls(newUrls);
  }

  const removeFile = async (index: number, image_url: string) => {
    setImagesToDeleteFromCloudinary((prev) => [...prev, image_url]);
    const newFiles = previews.filter((file, idx) => file != image_url);
    setPreviews(newFiles);
    const newUrls = urls.filter((file, idx) => file != image_url);
    setUrls(newUrls);
  };

  const thumbs = previews.map((file, index) => (
    <div key={`${file}-${index}`} className="group relative cursor-pointer">
      {!previewUrls && index >= urls.length ? (
        <div className="absolute flex w-full h-full items-center justify-center z-10 bg-opacity-50 bg-gray-200 rounded-md">
          <Spinner />
        </div>
      ) : null}
      <XMarkIcon
        className="absolute text-gray-500 h-5 w-5 p-0.5 right-1 top-1 z-20 shadow-md rounded-full bg-white opacity-80 hover:opacity-100"
        onClick={() => (!disabled ? removeFile(index, file) : null)}
      />
      <div className="aspect-w-1 aspect-h-1 rounded-md overflow-hidden">
        <Image
          src={file}
          alt={`Upload ${index + 1}`}
          fill
          style={{
            objectFit: "cover",
          }}
          className="w-full h-full object-center object-cover"
        />
      </div>
    </div>
  ));

  return (
    <div className="flex over flex-col">
      <label
        htmlFor="cover-photo"
        className="flex gap-x-2 text-sm font-medium text-gray-700"
      >
        {label}
        {labelSuggestion ? (
          <span className="font-normal text-gray-600">{labelSuggestion}</span>
        ) : null}
      </label>
      <div className="text-xs text-gray-500 pb-3">
        <p>
          Mínimo {minAllowed} fotografía{minAllowed > 1 ? "s" : ""}. Máximo{" "}
          {maxAllowed} fotografía{minAllowed > 1 ? "s" : ""}.
        </p>
        <p>PNG, JPG o WEBP hasta {MAX_ALLOWED_SIZE}MB c/u.</p>
      </div>
      <Dashboard
        id={`uppy-dashboard-${id}`}
        uppy={uppy}
        height={350}
        hideProgressAfterFinish={true}
        locale={{
          strings: {
            browseFiles: "Hacé click para buscar",
            dropPasteFiles: "Arrastrá las imágenes aquí o %{browseFiles}",
          },
        }}
      />
      {/* <div className="mt-3 w-full mx-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-x-2 gap-y-2">
        {thumbs}
      </div> */}

      {previews?.length ? (
        <Container
          removeFile={removeFile}
          previews={previews}
          setPreviews={setPreviews}
        />
      ) : null}
    </div>
  );
}

// PENDING:
// Remove image (remove from cloudinary via server side request)
// invalid state and style (react-hook form)
